/**
 * ServerSync - handles server communication for session sync
 *
 * Responsibilities:
 * - WebSocket connection for receiving server state updates
 * - HTTP fetch for initial state and library definitions
 * - HTTP POST for session sync (definitions, positions, viewport)
 *
 * All mutations happen locally in Runtime - server only syncs state.
 */

import type { SessionState } from '../server/SessionState';
import type { GraphDefinition } from '../graph/GraphDefinition';

import { ServerApi } from '../serverClient/ServerApi';
import { IRuntime } from '@/runtime/IRuntime';
import { IRuntimeStore } from '@/runtime/RuntimeStore';

const SYNC_DEBOUNCE_MS = 50;
export interface ServerSyncOptions {
  sessionId: string,
  store: IRuntimeStore;
  onWebSocketUpdate;
  /** WebSocket class to use (native WebSocket in browser, 'ws' package in Node) */
  WebSocketClass?: typeof WebSocket;
}

export type StateCallback = (state: SessionState) => void;

export class ServerSync {

  // Debounce state
  private syncTimers = new Map<string, ReturnType<typeof setTimeout>>();

  private onWebSocketUpdate: StateCallback | null = null;

  /** Whether server sync is enabled (default: false) */
  private _syncEnabled = false;

  serverApi: ServerApi;
  store: IRuntimeStore;

  /** Get sync enabled state */
  get syncEnabled(): boolean {
    return this._syncEnabled;
  }

  /** Enable or disable server sync */
  setSyncEnabled(enabled: boolean): void {
    this._syncEnabled = enabled;
  }

  constructor(options: ServerSyncOptions) {

    this.store = options.store;
    this.serverApi = new ServerApi(options.sessionId, options.WebSocketClass)
    this.onWebSocketUpdate = options.onWebSocketUpdate
    // Connect WebSocket for updates
    this.serverApi.connectWebSocket(this.onWebSocketUpdate.bind(this));

    // Subscribe to definition changes and sync to server
    this.store.subscribeDefinition((definitionId) => {
      const definition = this.store.getDefinition(definitionId);
      if (definition) {
        this.debouncedPostDefinition(definition);
      }
      // Also sync positions (they trigger definition change)
      if (definitionId === 'root') {
        const positions = this.store.getPositions(definitionId);
        this.debouncedSyncPositions(positions);
      }
    });
  }

  async init(): Promise<{ session: any; libraryDefinitions: Record<string, any> }> {
    await this.serverApi.waitUntilReady();

    // Load library definitions
    const libraryDefinitions = await this.serverApi.getAllLibraryDefinitions();
    const session = await this.serverApi.getSession();
    this.store.init(session, libraryDefinitions);

    return { session, libraryDefinitions };
  }

  /**
   * Set callback for when server pushes state updates
   */
  setWebsocketCallback(callback: StateCallback): void {
    this.onWebSocketUpdate = callback;
  }



  /**
 * Debounced post of a definition to server.
 * Multiple changes to the same definition will be batched.
 * WOULD BE CLEANER TO USE A LIBRARY DEBOUNCER

 */
  private debouncedPostDefinition(definition: GraphDefinition): void {
    if (!this._syncEnabled) return;

    const key = `postDef:${definition.id}`;
    const existing = this.syncTimers.get(key);
    if (existing) clearTimeout(existing);

    this.syncTimers.set(key, setTimeout(() => {
      this.syncTimers.delete(key);


        this.serverApi.postDefinition(definition);

    }, SYNC_DEBOUNCE_MS));
  }

  /**
   * Debounced sync with generic callback
   */
  private debouncedSync(key: string, callback: () => void): void {
    const existing = this.syncTimers.get(key);
    if (existing) clearTimeout(existing);

    this.syncTimers.set(key, setTimeout(() => {
      this.syncTimers.delete(key);
      callback();
    }, SYNC_DEBOUNCE_MS));
  }

  /**
   * Debounced sync of positions to server.
   */
  private debouncedSyncPositions(positions: Record<string, { x: number; y: number }>): void {
    if (!this._syncEnabled) return;

    this.debouncedSync('syncPositions', () => {
      this.serverApi.postSession({ positions });
    });
  }


  /**
* Flush pending definition syncs (for beforeunload)
*/
/*
  flushPendingChanges(): void {
    if (this.syncTimers.size === 0) return;

    // Find all pending definition syncs
    const pendingDefs: GraphDefinition[] = [];
    for (const key of this.syncTimers.keys()) {
      if (key.startsWith('postDef:')) {
        pendingDefIds.push(key.slice('postDef:'.length));
      }
    }

    // Clear all timers
    for (const timer of this.syncTimers.values()) {
      clearTimeout(timer);
    }
    this.syncTimers.clear();

    // Immediately post all pending definitions
    for (const defId of pendingDefIds) {
      const def = this.runtime.getGraphDefinition(defId);
      if (def) {
        this.serverApi.postDefinition(def);
      }
    }
  }
*/
  /**
   * Apply state from server.
   * Skips definitions that have pending local changes (debounced POST not yet sent).
   */
  async getSession(localDefinitions: Record<string, GraphDefinition>): Promise<SessionState> {
    // Filter definitions: keep local version for those with pending POSTs

    // Fetch initial state
    const session = await this.serverApi.getSession();

    const filteredDefinitions: typeof session.definitions = {};

    // First, preserve any local definitions with pending POSTs
    for (const key of this.syncTimers.keys()) {
      if (key.startsWith('postDef:')) {
        const defId = key.slice('postDef:'.length);
        const localDef = localDefinitions[defId];
        if (localDef) {
          filteredDefinitions[defId] = localDef;
        }
      }
    }

    // Then add server definitions (but don't overwrite pending ones)
    for (const [defId, def] of Object.entries(session.definitions)) {
      if (!filteredDefinitions[defId]) {
        filteredDefinitions[defId] = def;
      }
    }


    return {
      definitions: filteredDefinitions,
      positions: session.positions,
      viewport: session.viewport
    }


    // Notify of external update (after init, to clear local history)
    /*  if (this.initialized) {
        this.onExternalUpdate?.();
      }*/


  }




  /**
   * Dispose and clean up
   */
  dispose(): void {
    this.serverApi.dispose()
    this.onWebSocketUpdate = null;
  }
}
